import React, { useEffect, useMemo } from 'react'
import View from '../View'
import Breadcrumb from '../Breadcrumb'
import { createStructuredSelector } from 'reselect'
import { useSelector } from 'react-redux'
import Loader from 'components/Loader'
import { LeftSidebarView, HeaderButton } from 'components/Sidebar'
import { useBoardDataProvider, BoardContext } from 'dataProviders/Board'
import { Route, useHistory } from 'react-router'
import { Location } from 'history'
import classes from './Layout.module.scss'
import KanBoIcon from 'components/KanBoIcon'
import { navigationController } from 'components/Modal'
import { useCardDataProvider, CardContext } from 'dataProviders/Card'
import { useIntl } from 'react-intl'
import sweetalert from 'sweetalert2'
import BoardBackground from 'routes/Board/Layout/BoardBackground'
import useBoardBackgroundStyle from 'routes/Board/Layout/useBoardBackgroundStyle'
import styled from 'styled-components'

const CardBackground = styled(BoardBackground)`
  border-radius: 5px;
`

type LocationState = {
  cards?: any[],
  fromLocation?: Location<LocationState>
}

type StateProps = {
  boardId: number,
}

type Props = {
  id: number
  location: Location<LocationState>
  isActive: boolean
  modalId?: string
  modalMaximized?: boolean
  transitionEnd?: boolean
}

const Layout: React.FC<Props> = ({
  id,
  location,
  isActive,
  modalId,
  modalMaximized,
  transitionEnd,
}) => {
  const intl = useIntl()

  const { set } = navigationController.useActions()

  const history = useHistory()

  const {
    boardId,
  } = useSelector(useMemo(() => createStructuredSelector<any, StateProps>({
    boardId: state => id && state['view-board'].cards[id]?.boardId,
  }), [id]))

  const { isReady: isCardReady } = useCardDataProvider(id)

  const {
    isLoaded: isBoardLoaded,
    isReady: isBoardReady,
    isClosed: isBoardClosed
  } = useBoardDataProvider({ id: boardId })

  useEffect(() => {
    if (id && isBoardLoaded) {
      if (isBoardClosed) {
        sweetalert({
          type: 'error',
          text: intl.formatMessage({ id: 'BOARD_CLOSED' })
        })
          .catch(sweetalert.noop)
          .finally(() => {
            history.goBack()
          })
      }
    }
  }, [id, isBoardLoaded, isBoardClosed])

  const isInitialized = useMemo(() => {
    return (
      isCardReady &&
      isBoardReady &&
      transitionEnd
    )
  }, [isCardReady, isBoardReady, transitionEnd])

  const cardContextValue = useMemo(() => ({
    id
  }), [id])

  const boardContextValue = useMemo(() => ({
    id: boardId
  }), [boardId])

  const boardBackgroundStyle = useBoardBackgroundStyle(boardId)

  return (
    <Route
      location={location}
    >
      <CardContext.Provider value={cardContextValue}>
        <BoardContext.Provider value={boardContextValue}>
          <CardBackground style={boardBackgroundStyle}>
            <LeftSidebarView
              className={`${classes.layout}${modalMaximized ? ` ${classes.maximized}` : ''}`}
              title={isInitialized && <Breadcrumb />}
              inlineMenu={
                modalId &&
                  <HeaderButton
                    icon={<KanBoIcon name={modalMaximized ? 'kanbo-16-hide-details' : 'kanbo-16-show-details'} />}
                    onClick={() => {
                      set(modalId, { maximized: !modalMaximized })
                    }}
                />
              }
            >
              {(isInitialized && isActive) ? <View /> : <Loader absoluteCenter />}
            </LeftSidebarView>
          </CardBackground>
        </BoardContext.Provider>
      </CardContext.Provider>
    </Route>
  )
}

export default Layout
